import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import { Music, Edit, Search, ChevronLeft, Save, X, Disc } from 'lucide-react';

export default function MetadataEditor() {
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingTrack, setEditingTrack] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [limit] = useState(50);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    artist: '',
    album: '',
    label: '',
    producer: '',
    genre: '',
    bpm: '',
    key: '',
    year: '',
    duration: ''
  });

  useEffect(() => {
    fetchTracks();
  }, [currentPage]);

  const fetchTracks = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      setLoading(true);
      const response = await axios.get(`${API}/admin/tracks/all?skip=${currentPage * limit}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setTracks(response.data.tracks);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching tracks:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/login');
      } else {
        toast.error('Failed to load tracks');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditTrack = (track) => {
    setEditingTrack(track);
    setFormData({
      title: track.title || '',
      artist: track.artist || '',
      album: track.album || '',
      label: track.label || '',
      producer: track.producer || '',
      genre: track.genre || '',
      bpm: track.bpm || '',
      key: track.key || '',
      year: track.year || '',
      duration: track.duration || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveMetadata = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Only send fields that have changed
      const updates = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== (editingTrack[key] || '')) {
          updates[key] = formData[key];
        }
      });

      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save');
        return;
      }

      await axios.put(`${API}/admin/tracks/${editingTrack.id}/metadata`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Metadata updated successfully!');
      setShowEditDialog(false);
      fetchTracks(); // Refresh the list
    } catch (error) {
      console.error('Error updating metadata:', error);
      toast.error(error.response?.data?.detail || 'Failed to update metadata');
    }
  };

  const filteredTracks = tracks.filter(track => 
    searchTerm === '' || 
    track.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    track.artist?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    track.album?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(total / limit);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading tracks...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Disc className="w-10 h-10 text-cyan-400 animate-spin" style={{animationDuration: '3s'}} />
            <div>
              <h1 className="text-3xl font-bold text-white">Metadata Editor</h1>
              <p className="text-gray-400">Edit song information across your library</p>
            </div>
          </div>
          <Button onClick={() => navigate('/admin')} variant="outline" className="bg-gray-700 hover:bg-gray-600 border-gray-600">
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
        </div>

        {/* Stats Card */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Tracks in Library</p>
                <p className="text-3xl font-bold text-white mt-1">{total}</p>
              </div>
              <Music className="w-12 h-12 text-cyan-400" />
            </div>
          </CardContent>
        </Card>

        {/* Search Bar */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by title, artist, or album..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-gray-900 border-gray-600 text-white"
              />
            </div>
          </CardContent>
        </Card>

        {/* Tracks Table */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Track Library</CardTitle>
            <CardDescription className="text-gray-400">
              Click edit to modify track metadata
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[600px]">
              <table className="w-full">
                <thead className="sticky top-0 bg-gray-800 z-10">
                  <tr className="border-b border-gray-700">
                    <th className="text-left p-3 text-gray-400 font-semibold">Track Info</th>
                    <th className="text-left p-3 text-gray-400 font-semibold">Album</th>
                    <th className="text-left p-3 text-gray-400 font-semibold">Label</th>
                    <th className="text-left p-3 text-gray-400 font-semibold">Producer</th>
                    <th className="text-left p-3 text-gray-400 font-semibold">BPM / Key</th>
                    <th className="text-right p-3 text-gray-400 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTracks.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center p-8 text-gray-400">
                        No tracks found
                      </td>
                    </tr>
                  ) : (
                    filteredTracks.map(track => (
                      <tr key={track.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="text-white font-medium">{track.title || 'Untitled'}</span>
                            <span className="text-sm text-gray-400">{track.artist || 'Unknown Artist'}</span>
                            {track.genre && (
                              <Badge className="w-fit mt-1 bg-purple-600 text-xs">{track.genre}</Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-gray-300 text-sm">
                          {track.album || '-'}
                        </td>
                        <td className="p-3 text-gray-300 text-sm">
                          {track.label || '-'}
                        </td>
                        <td className="p-3 text-gray-300 text-sm">
                          {track.producer || '-'}
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col text-sm">
                            <span className="text-cyan-400">{track.bpm ? `${track.bpm} BPM` : '-'}</span>
                            <span className="text-purple-400">{track.key || '-'}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-end">
                            <Button
                              size="sm"
                              onClick={() => handleEditTrack(track)}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </ScrollArea>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                <p className="text-sm text-gray-400">
                  Showing {currentPage * limit + 1} - {Math.min((currentPage + 1) * limit, total)} of {total}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                    className="bg-gray-700 hover:bg-gray-600"
                  >
                    Previous
                  </Button>
                  <span className="text-white px-4 py-2">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <Button
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="bg-gray-700 hover:bg-gray-600"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Metadata Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Edit Track Metadata</DialogTitle>
            <DialogDescription className="text-gray-400">
              Update song information for: {editingTrack?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Title */}
              <div className="col-span-2 space-y-2">
                <Label className="text-white">Song Title *</Label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="bg-gray-900 border-gray-600 text-white"
                  placeholder="Enter song title"
                />
              </div>

              {/* Artist */}
              <div className="space-y-2">
                <Label className="text-white">Artist *</Label>
                <Input
                  type="text"
                  value={formData.artist}
                  onChange={(e) => setFormData({...formData, artist: e.target.value})}
                  className="bg-gray-900 border-gray-600 text-white"
                  placeholder="Enter artist name"
                />
              </div>

              {/* Producer */}
              <div className="space-y-2">
                <Label className="text-white">Producer</Label>
                <Input
                  type="text"
                  value={formData.producer}
                  onChange={(e) => setFormData({...formData, producer: e.target.value})}
                  className="bg-gray-900 border-gray-600 text-white"
                  placeholder="Enter producer name"
                />
              </div>

              {/* Album */}
              <div className="space-y-2">
                <Label className="text-white">Album</Label>
                <Input
                  type="text"
                  value={formData.album}
                  onChange={(e) => setFormData({...formData, album: e.target.value})}
                  className="bg-gray-900 border-gray-600 text-white"
                  placeholder="Enter album name"
                />
              </div>

              {/* Label */}
              <div className="space-y-2">
                <Label className="text-white">Record Label</Label>
                <Input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({...formData, label: e.target.value})}
                  className="bg-gray-900 border-gray-600 text-white"
                  placeholder="Enter record label"
                />
              </div>

              {/* Genre */}
              <div className="space-y-2">
                <Label className="text-white">Genre</Label>
                <Input
                  type="text"
                  value={formData.genre}
                  onChange={(e) => setFormData({...formData, genre: e.target.value})}
                  className="bg-gray-900 border-gray-600 text-white"
                  placeholder="Enter genre"
                />
              </div>

              {/* Year */}
              <div className="space-y-2">
                <Label className="text-white">Year</Label>
                <Input
                  type="text"
                  value={formData.year}
                  onChange={(e) => setFormData({...formData, year: e.target.value})}
                  className="bg-gray-900 border-gray-600 text-white"
                  placeholder="e.g., 2024"
                />
              </div>

              {/* BPM */}
              <div className="space-y-2">
                <Label className="text-white">BPM</Label>
                <Input
                  type="number"
                  value={formData.bpm}
                  onChange={(e) => setFormData({...formData, bpm: e.target.value})}
                  className="bg-gray-900 border-gray-600 text-white"
                  placeholder="e.g., 128"
                />
              </div>

              {/* Key */}
              <div className="space-y-2">
                <Label className="text-white">Key</Label>
                <Input
                  type="text"
                  value={formData.key}
                  onChange={(e) => setFormData({...formData, key: e.target.value})}
                  className="bg-gray-900 border-gray-600 text-white"
                  placeholder="e.g., Am, C Major"
                />
              </div>

              {/* Duration */}
              <div className="space-y-2">
                <Label className="text-white">Duration (seconds)</Label>
                <Input
                  type="number"
                  value={formData.duration}
                  onChange={(e) => setFormData({...formData, duration: e.target.value})}
                  className="bg-gray-900 border-gray-600 text-white"
                  placeholder="e.g., 180"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-700">
              <Button 
                onClick={handleSaveMetadata} 
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
              <Button 
                onClick={() => setShowEditDialog(false)} 
                variant="outline" 
                className="flex-1 bg-gray-700 border-gray-600"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
